import React, { useEffect, useState } from "react";
import "./GithubActivity.css";

const USERNAME = "Ghassen-Ben-Nasser";
const API_URL = `https://github-contributions-api.jogruber.de/v4/${USERNAME}?y=last`;

const DAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// API returns a flat array of { date, count, level } - group into Sun-anchored weeks
function groupIntoWeeks(days) {
  if (!days || !days.length) return [];
  const weeks = [];
  let week = [];

  // Pad the start so first day lands on correct weekday (0=Sun)
  const firstDow = new Date(days[0].date).getDay();
  for (let i = 0; i < firstDow; i++) {
    week.push(null);
  }

  days.forEach((day) => {
    week.push(day);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  });

  if (week.length) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  return weeks;
}

function hexToRgb(hex) {
  const h = (hex || "").replace("#", "").slice(0, 6);
  if (h.length < 6) return { r: 100, g: 100, b: 100 };
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function levelColor(level, highlightHex, isDark) {
  if (level === 0 || level == null)
    return isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
  const { r, g, b } = hexToRgb(highlightHex);
  const alpha = [0.25, 0.5, 0.75, 1][level - 1];
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function GithubActivity({ theme }) {
  const [weeks, setWeeks] = useState([]);
  const [total, setTotal] = useState(null);
  const [error, setError] = useState(false);

  const isDark = theme.body === "#25303B";

  useEffect(() => {
    let cancelled = false;
    fetch(API_URL)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        // data.contributions is a flat array of { date, count, level }
        const days = Array.isArray(data.contributions)
          ? data.contributions
          : [];
        const grouped = groupIntoWeeks(days);
        setWeeks(grouped);
        const sum = days.reduce((acc, d) => acc + (d.count || 0), 0);
        setTotal(sum);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const BLOCK = 13;
  const GAP = 4;
  const STEP = BLOCK + GAP;

  // Month labels: track the first week column a new month appears in
  const monthLabels = [];
  if (weeks.length) {
    let lastMonth = -1;
    weeks.forEach((week, wi) => {
      const firstReal = week.find((d) => d !== null);
      if (firstReal) {
        const m = new Date(firstReal.date).getMonth();
        if (m !== lastMonth) {
          monthLabels.push({ x: wi * STEP, label: MONTHS[m] });
          lastMonth = m;
        }
      }
    });
  }

  const svgWidth = weeks.length * STEP;

  return (
    <div
      className="github-activity-section"
      style={{ backgroundColor: theme.body }}
    >
      <div className="github-activity-container">
        <p
          className="github-activity-label"
          style={{ color: theme.secondaryText }}
        >
          Activity
        </p>
        <h2 className="github-activity-title" style={{ color: theme.text }}>
          {total !== null
            ? `${total.toLocaleString()} contributions in the last year`
            : "GitHub Contributions"}
        </h2>

        {error && (
          <p style={{ color: theme.secondaryText, fontSize: 14 }}>
            Could not load contribution data.
          </p>
        )}

        {!error && weeks.length === 0 && (
          <div
            className="github-skeleton"
            style={{ backgroundColor: theme.text + "10" }}
          />
        )}

        {!error && weeks.length > 0 && (
          <div className="github-calendar-wrapper">
            <div className="github-calendar-inner">
              {/* Day-of-week labels */}
              <div className="github-day-labels" style={{ paddingTop: 20 }}>
                {DAY_LABELS.map((d, i) => (
                  <span
                    key={i}
                    className="github-day-label"
                    style={{
                      color: theme.secondaryText,
                      height: STEP,
                      lineHeight: STEP + "px",
                    }}
                  >
                    {d}
                  </span>
                ))}
              </div>

              {/* SVG calendar */}
              <div className="github-svg-scroll">
                <svg
                  width={svgWidth}
                  height={7 * STEP - GAP + 24}
                  style={{ display: "block" }}
                >
                  {/* Month labels row */}
                  {monthLabels.map(({ x, label }, i) => (
                    <text
                      key={i}
                      x={x}
                      y={12}
                      fontSize={11}
                      fill={theme.secondaryText}
                      fontFamily="inherit"
                    >
                      {label}
                    </text>
                  ))}

                  {/* Contribution blocks */}
                  <g transform="translate(0, 20)">
                    {weeks.map((week, wi) =>
                      week.map((day, di) => (
                        <rect
                          key={`${wi}-${di}`}
                          x={wi * STEP}
                          y={di * STEP}
                          width={BLOCK}
                          height={BLOCK}
                          rx={2}
                          ry={2}
                          fill={levelColor(
                            day ? day.level : 0,
                            theme.imageHighlight,
                            isDark
                          )}
                        >
                          {day && (
                            <title>
                              {`${day.count} contribution${
                                day.count !== 1 ? "s" : ""
                              } on ${day.date}`}
                            </title>
                          )}
                        </rect>
                      ))
                    )}
                  </g>
                </svg>
              </div>
            </div>

            {/* Legend */}
            <div
              className="github-legend"
              style={{ color: theme.secondaryText }}
            >
              <span>Less</span>
              {[0, 1, 2, 3, 4].map((l) => (
                <svg
                  key={l}
                  width={BLOCK}
                  height={BLOCK}
                  style={{ display: "inline-block", verticalAlign: "middle" }}
                >
                  <rect
                    width={BLOCK}
                    height={BLOCK}
                    rx={2}
                    ry={2}
                    fill={levelColor(l, theme.imageHighlight, isDark)}
                  />
                </svg>
              ))}
              <span>More</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
